import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Loader2, Coins } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { uploadImage } from "@/lib/imageUpload";

function PostPhoto({ bonus, onDone }) {
  const ref = useRef(null);
  const [img, setImg] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const pick = async (f) => {
    if (!f) return;
    setBusy(true);
    try { setImg((await uploadImage("/feed/upload", f)).url); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post("/mug-gift/photo", { image: img, text }); toast.success(`Danke! +${data.points} Punkte ☕`, { description: "Dein Foto ist jetzt im Feed und in der Galerie." }); onDone(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-[2rem] bg-[#121212] text-white p-5 space-y-3 ring-1 ring-amber-300/25" data-testid="mug-photo-form">
      <p className="font-display text-lg font-black">Zeig uns deine Tasse! ☕</p>
      <p className="text-sm text-white/70 flex items-center gap-1.5"><Coins className="w-4 h-4 text-amber-300" /> Einmalig <b className="text-amber-300">+{bonus} Bonuspunkte</b> für dein Foto – es erscheint im Feed und hier in der Galerie.</p>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="mug-photo-input" />
      {img ? <img src={img} alt="Dein Tassen-Foto" className="w-full max-h-72 object-cover rounded-2xl" data-testid="mug-photo-preview" />
        : <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="w-full h-32 rounded-2xl border-2 border-dashed border-amber-300/50 flex flex-col items-center justify-center gap-2 text-amber-200 font-bold" data-testid="mug-photo-pick">
            {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <Camera className="w-7 h-7" />} Foto mit deiner Tasse wählen
          </button>}
      <input value={text} onChange={(e) => setText(e.target.value)} maxLength={300} placeholder="Ein paar Worte dazu (optional)" className="w-full h-11 rounded-full bg-white/10 px-4 text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="mug-photo-text" />
      <button type="button" onClick={send} disabled={!img || busy} className="w-full h-12 rounded-full bg-gradient-to-r from-amber-300 to-amber-500 text-[#3a2508] font-black disabled:opacity-50" data-testid="mug-photo-submit">Foto posten & Punkte holen</button>
    </div>
  );
}

export function MugGallery({ d, onChange }) {
  return (
    <section className="space-y-3" data-testid="mug-gallery">
      <h2 className="font-display text-lg font-extrabold text-slate-800">Eure Tassen ☕</h2>
      {d.can_post_photo && <PostPhoto bonus={d.photo_bonus} onDone={onChange} />}
      {!d.is_buyer && <p className="text-sm text-slate-500">Hast du die Dankeschön-Tasse bekommen? Sobald sie bei dir ist, kannst du hier ein Foto posten und bekommst <b>+{d.photo_bonus} Punkte</b>.</p>}
      {d.photos.length ? (
        <ul className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {d.photos.map((p) => (
            <li key={p.id} className="rounded-3xl overflow-hidden lux-card" data-testid={`mug-photo-${p.id}`}>
              <Link to={`/post/${p.id}`}><img src={p.images[0]} alt={`Tasse von ${p.user?.name || "Mitglied"}`} className="w-full aspect-square object-cover" loading="lazy" /></Link>
              <p className="px-3 py-2 text-xs font-bold text-slate-700 truncate">{p.user?.name || "Mitglied"}</p>
            </li>
          ))}
        </ul>
      ) : <p className="text-sm text-slate-400" data-testid="mug-gallery-empty">Noch keine Tassen-Fotos – das erste könnte deins sein!</p>}
    </section>
  );
}
